import { cn } from '@/utils';
import { Dialog } from '@/components/Dialog';
import { Textarea } from '@/components/ui/textarea';
import { GenerateIcon, EditorIntroIcon, EditorExtendIcon, EditorOutroIcon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { postAudioGenerateEditor } from '@/service/editor';
import useGeneratorStore from '@/state/generator-store';
import { EditorOperatorEnum } from '@/state/editor-store';
import { prepareSimpleEditorParams, SimpleEditorInput } from '@/pages/Home/Generator/utils';
import { useCallback, useState } from 'react';
import { useRequest } from 'ahooks';
import { toast } from 'sonner';
import JenErrorToast from '@/components/ui/jen-error-toast';

export const useMobileEditorDialog = ({ id }: { id: string }) => {
  const onShowEditorDialog = () => {
    Dialog.basic({
      closeClassName: 'left-auto right-4 rounded-[4px] text-white bg-white/20 hover:bg-white/30',
      closeColor: '#ffffff',
      dialogContentClassName: 'bg-black text-white',
      contentJSX: ({ onClose }) => {
        const [mode, setMode] = useState<EditorOperatorEnum>(EditorOperatorEnum.NONE);
        const [prompt, setPrompt] = useState('');
        const { onSubmit } = useModel(onClose);

        const onGenerate = () => {
          onSubmit({
            audioId: id,
            editorDuration: mode === EditorOperatorEnum.EXTEND ? 60 : 10,
            trackName: '',
            userPrompt: prompt.trim(),
            mode,
            start: 0,
            end: 60,
          });
        };

        return (
          <>
            <div className="p-4">
              <div className="text-jen-size-heading-02 mt-2 font-['NB International Pro']">Edit Track</div>
              <div className="mt-6">
                <div className="flex space-x-2">
                  <EditModeButton
                    mode={mode === EditorOperatorEnum.INTRO ? 'active' : 'normal'}
                    onClick={() => setMode(EditorOperatorEnum.INTRO)}
                  >
                    <div className="flex w-full justify-between">
                      <EditorIntroIcon />
                      <span>+10s</span>
                    </div>
                    <div className="mt-4">INTRO</div>
                  </EditModeButton>
                  <EditModeButton
                    mode={mode === EditorOperatorEnum.EXTEND ? 'active' : 'normal'}
                    onClick={() => setMode(EditorOperatorEnum.EXTEND)}
                  >
                    <div className="flex w-full justify-between">
                      <EditorExtendIcon />
                      <span>+60s</span>
                    </div>
                    <div className="mt-4">EXTEND</div>
                  </EditModeButton>
                  <EditModeButton
                    mode={mode === EditorOperatorEnum.OUTRO ? 'active' : 'normal'}
                    onClick={() => setMode(EditorOperatorEnum.OUTRO)}
                  >
                    <div className="flex w-full justify-between">
                      <EditorOutroIcon />
                      <span>+10s</span>
                    </div>
                    <div className="mt-4">OUTRO</div>
                  </EditModeButton>
                </div>
                <div className="text-jen-size-heading-04 text-white/50 mt-6 mb-2">PROMPT</div>
                <Textarea
                  placeholder='IMAGINE "SOUFUL RAP, HI HATS..."'
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="bg-gradient-to-r from-[#191A1B] to-[#1E2021] text-white text-lg px-4 rounded-lg border-jen-dark-grey-60 min-h-40 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none resize-none touch-none placeholder-[#AAA7A7]"
                />
              </div>
            </div>
            <GenerateButton isDisabled={!mode || mode === EditorOperatorEnum.NONE || !prompt.trim()} onGenerate={onGenerate} />
          </>
        );
      },
    });
  };

  return { onShowEditorDialog };
};

const useModel = (closeDialog: () => void) => {
  const { setGeneratorResult } = useGeneratorStore();
  const { run: runPostAudioGenerateEditor, loading: generateEditorLoading } = useRequest(postAudioGenerateEditor, {
    manual: true,
    onSuccess: (data) => {
      setGeneratorResult(data);
      closeDialog();
    },
    onError: (error) => {
      toast(<JenErrorToast error={error} />, {
        className: '!border-0 !shadow-none',
        unstyled: true,
      });
    },
    debounceWait: 500,
  });

  const onSubmit = useCallback((input: SimpleEditorInput) => {
    const params = prepareSimpleEditorParams(input);
    runPostAudioGenerateEditor(params as any);
  }, []);

  return {
    onSubmit,
  };
};

const EditModeButton = (props: { mode: 'normal' | 'active'; onClick: () => void; children: React.ReactNode }) => {
  const { mode, onClick, children } = props;

  const dynamicClassName = mode === 'normal' ? 'from-[#383A3C] to-[#2B2E30] text-white/70' : 'from-[#FF5B27] to-[#C24017] text-white/90';

  return (
    <Button
      className={cn('flex-col flex-1 !items-start !px-2 h-20 bg-gradient-to-r from-[#383A3C] to-[#2B2E30', dynamicClassName)}
      onClick={onClick}
    >
      {children}
    </Button>
  );
};

const GenerateButton = (props: { isDisabled: boolean; onGenerate: () => void }) => {
  const { isDisabled, onGenerate } = props;
  return (
    <Button
      className="flex flex-col w-full !h-20 py-2 px-2 bg-jen-orange text-lg hover:bg-jen-orange/95 text-accent-foreground rounded-none rounded-bl-2xl rounded-br-2xl"
      size="full"
      disabled={isDisabled}
      onClick={onGenerate}
    >
      <div className="relative flex items-center justify-center w-full h-[90%] bg-jen-orange rounded-xl shadow-[0_20px_30px_rgba(0,0,0,0.3)]">
        <span className="flex items-center">
          <GenerateIcon />
        </span>
        <div className="flex">
          <span className="flex items-center font-['NB International Pro'] text-[1.2rem] ml-2 font-medium leading-[24px] tracking-[-0.02em] text-left text-white">
            Jenerate
          </span>
          <div className="text-white text-[10px] ml-2 font-normal font-['NB International Pro']">1 CRD</div>
        </div>
      </div>
    </Button>
  );
};
